import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, Input, Select, message } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { OutfitGrid, OutfitDetailView, useOutfitLibrary } from './shared'
import CreateOutfitModal from './CreateOutfitModal'
import './WardrobeLibraryPickerModal.scss'

const { Search } = Input
const { Option } = Select

/**
 * Modal for browsing and selecting outfits from the library
 *
 * @param {Object} props
 * @param {Boolean} props.visible - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSelect - Selection handler (imageUrl, tag) => void
 * @param {String} props.targetPlaceholder - Placeholder being assigned to
 * @param {Object} props.initialOutfit - Initial outfit to show in view mode
 * @param {String} props.mode - 'view' or 'select'
 * @param {Object} props.formData - Form data containing outfits
 * @param {Function} props.setFormData - Function to update form data
 */
const WardrobeLibraryPickerModal = ({
  visible,
  onClose,
  onSelect,
  targetPlaceholder,
  initialOutfit = null,
  mode = 'select',
  formData = {},
  setFormData = () => {},
  videoId
}) => {
  const {
    createOutfit,
    isCreatingOutfit: isCreating,
    creationProgress: progress,
    stopPolling
  } = useWardrobe({ videoId })

  const {
    outfits: libraryOutfits,
    isLoading: isLibraryLoading,
    fetchIfNeeded,
    refresh,
    goToPage,
    pagination
  } = useOutfitLibrary({ formData, setFormData, videoId })

  const [selectedOutfit, setSelectedOutfit] = useState(null)
  const [activeView, setActiveView] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({ style: '', season: '' })
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)

  console.log({ initialOutfit, selectedOutfit, mode, visible })

  useEffect(() => {
    if (visible) {
      // Use the centralized hook that checks formData
      fetchIfNeeded()

      if (mode === 'view' && initialOutfit) {
        setSelectedOutfit(initialOutfit)
        // Let OutfitDetailView handle the initial view
      } else {
        setSelectedOutfit(null)
        setActiveView(null)
      }
    }
    return () => stopPolling()
  }, [visible])

  const handleOutfitClick = outfit => {
    setSelectedOutfit(outfit)
    setActiveView(null) // Let OutfitDetailView set the initial view
  }

  const handleApply = (view, outfit) => {
    if (outfit && view) {
      onSelect && onSelect(view.url, outfit.tag || outfit.id)
      message.success(`Selected ${view.label} view of ${outfit.name}`)
      onClose()
    }
  }

  const handleBack = () => {
    setSelectedOutfit(null)
    setActiveView(null)
    // Only close if this is a pure view-only modal (no selection capability)
    // If onSelect exists, it means we came from Selector Modal and should show library
    if (mode === 'view' && initialOutfit && !onSelect) {
      onClose()
    }
  }

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    refresh({ action: 'filter', ...newFilters })
  }

  const handleSearch = () => {
    refresh({ action: 'search', query: searchQuery })
  }

  const renderLibraryGrid = () => (
    <>
      <div className="modal-header-v2">
        <h2 className="modal-title-v2">Browse Wardrobe</h2>
        <p className="modal-subtitle-v2">
          Assigning to:{' '}
          {targetPlaceholder
            ? targetPlaceholder.replace(/^_{2}|_{2}$/g, '')
            : '...'}
        </p>
      </div>

      <div className="modal-body-v2">
        <div className="toolbar-v2">
          <Search
            placeholder="Search outfits..."
            onChange={e => setSearchQuery(e.target.value)}
            onPressEnter={handleSearch}
            value={searchQuery}
            className="custom-search-input"
            allowClear
          />

          <div className="filter-row-v2">
            <Select
              placeholder="Style"
              value={filters.style}
              onChange={val => handleFilter('style', val)}
            >
              <Option value="">All Styles</Option>
              <Option value="formal">Formal</Option>
              <Option value="casual">Casual</Option>
              <Option value="sporty">Sporty</Option>
            </Select>

            <Select
              placeholder="Season"
              value={filters.season}
              onChange={val => handleFilter('season', val)}
            >
              <Option value="">All Seasons</Option>
              <Option value="summer">Summer</Option>
              <Option value="winter">Winter</Option>
            </Select>

            <Button
              type="primary"
              onClick={() => setIsCreateModalVisible(true)}
            >
              <Icon type="plus" /> Add New Outfit
            </Button>

            <Button onClick={() => refresh()} loading={isLibraryLoading}>
              <Icon type="reload" />
            </Button>
          </div>
        </div>

        <OutfitGrid
          outfits={libraryOutfits}
          onItemClick={handleOutfitClick}
          isLoading={isLibraryLoading}
          emptyMessage="No outfits found in your library."
          pagination={pagination}
          onPageChange={goToPage}
        />
      </div>
    </>
  )

  const renderDetailView = () => (
    <OutfitDetailView
      outfit={selectedOutfit}
      activeView={activeView}
      onViewChange={setActiveView}
      onApply={onSelect ? handleApply : null}
      onBack={handleBack}
      onClose={onClose}
      mode={mode}
    />
  )

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={selectedOutfit ? 1000 : 800}
        className="wardrobe-library-picker-modal-v2"
      >
        {selectedOutfit ? renderDetailView() : renderLibraryGrid()}
      </Modal>

      <CreateOutfitModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
        onSuccess={outfitData => {
          refresh()
          if (outfitData && mode === 'select') {
            handleOutfitClick(outfitData)
          }
        }}
        createOutfit={createOutfit}
        isCreating={isCreating}
        creationProgress={progress}
      />
    </>
  )
}

export default WardrobeLibraryPickerModal
